/**
 * THE HEARING (MusicalFrame 1.3 frame.drivers — what the fx worlds react to). The engine's attribution
 * student needs the CONTINUOUS mono mix at exact 60 Hz hops, so a tiny worklet (public/pcm-tap-worklet.js)
 * taps the same node the StereoAnalyser reads — on a TV that is the jitter-buffered PLAYOUT, so drivers sit on
 * the sound — and posts ~21 ms blocks of it.
 *
 * WHERE THE STUDENT RUNS (engineApi 1.18): in a worker (hearing.worker.ts), fed straight from the worklet over
 * a MessagePort — the audio never touches the main thread, which only reads the latest driver frame. In-thread
 * the hearing cost a weak TV's main thread 600–700 ms per second of audio. The in-thread path stays only as the
 * fallback where the worker cannot run (no Worker, an OTA engine without DriverWorkerClient or speaking another
 * protocol, a worker that fails to start): then the worklet posts to the main thread and each frame drains it
 * into the analyzer (MusicalAnalyzer.pushPcm). One decision per binding, made when the hearing is first needed.
 *
 * GATED (Hearing.listen): only renderers that READ frame.drivers listen (VisualMode.consumesDrivers — the fx family
 * and the lite line), so the tap is silent unless one is on stage, incoming or prewarming (VisualHost.wantsDrivers,
 * which also knows a renderer that fell back to the starfield). Measured, a cold hearing agrees with a warm one within 1–2 s (mean level error 0.03
 * at 1 s, 0.007 at 2 s) and the Director's prewarm lead covers that before a cut.
 */
import type { DriverWorkerClient, MusicalAnalyzer } from '@syqel/engine'
import { Engine } from '@syqel/ota-client'
import { createProcessorNode, type PortNode } from '../processorNode'
import { publicUrl } from '../publicUrl'
import { startHearingWorker } from './hearingWorker'

// the attribution student — listener r1 (uni-r1-c64, syqel-sdk tools/attribution-lab/student/weights/candidates,
// sha256 38314899c7bb953b…): the engine reads it with the measured r1 profile (per-lane drum knees, family
// drivers) because of its heads, nothing to configure here. A new model = a new file name.
const HEARING_WEIGHTS_URL = publicUrl('/hearing/uni-r1-c64.bin')
const HEARING_MAX_QUEUED_SEC = 3 // in-thread: nobody draining (a hidden tab) must not grow the queue without bound
const WORKER_START_TIMEOUT_MS = 10_000 // a worker that never answers `ready` is a worker that failed to start

type Analyzer = Pick<MusicalAnalyzer, 'pushPcm' | 'resetDrivers' | 'setAttributionWeights'> & Partial<Pick<MusicalAnalyzer, 'setDriverSource'>>

/** Where the student runs for a binding: not yet decided, a worker, the main thread, or nowhere (no weights). */
export type HearingMode = 'pending' | 'worker' | 'thread' | 'off'

export interface HearingStats {
  mode: HearingMode
  /** Milliseconds per second the student spent analysing since the previous stats() — on the worker's thread
   *  in 'worker' mode, on the MAIN thread in 'thread' mode. */
  busyMsPerSec: number
  /** How the engine reads the loaded student (LiveDriverProfile.id: 'drum-loudness-r1' for r1, 'legacy' for an
   *  older file), or null before the weights are in. */
  reading: string | null
}

/** A render loop's hearing: one per analyzer, for the loop's life. */
export interface Hearing {
  /** Each frame, before the analyzer's update: in-thread, drains the tapped PCM since the last frame. */
  feed(): void
  /** Each frame, once the stage is decided: does anything on stage listen? Only a change does anything. */
  listen(on: boolean): void
  stats(): HearingStats
  dispose(): void
}

interface Binding {
  analyzer: Analyzer
  mode: HearingMode
  client: DriverWorkerClient | null
  /** ms spent analysing as of the last stats(): the main thread's pushPcm in-thread, the worker's in a worker */
  busyMs: number
  busySeen: number
}

let tap: PortNode | null = null
let tapRate = 48_000
let tapEpoch = 0 // bumped per source: a tap still loading for an older source is never wired
let gateOn = false
let bound: Binding | null = null
let queue: Float32Array[] = [] // in-thread only: blocks the worklet posted to the main thread
let queued = 0

/** Where the voice orbs take their audio (VisualHost.pushOrbPcm / resetOrb) — set by the one render loop that
 *  draws them, and only where renderPolicy orbFor enables them (never on a TV). */
export interface OrbSink {
  push(left: Float32Array, right: Float32Array, sampleRate: number): void
  /** the audio jumped (a new source, a new track, a seek): tails and pitch tracking start over */
  reset(): void
}
let orb: OrbSink | null = null
let orbPcm = false

/** The voice orbs take their resets from this tap — and, when they follow the voice's pitch (`pcm`), its stereo
 *  blocks (same node the hearing reads); null stops both. */
export function hearOrb(sink: OrbSink | null, pcm = true): void {
  const fed = !!sink && pcm
  if (sink === orb && fed === orbPcm) return
  orb = sink
  orbPcm = fed
  tap?.port.postMessage({ orb: orbPcm })
}

/** The audio jumped without a new node — the phone shell's next track or seek, a media element's seek. The
 *  orbs start over; the hearing keeps its own continuity (its student re-converges within 1–2 s). */
export function audioDiscontinuity(): void {
  orb?.reset()
}

/** A new source's node (audio/graph.ts analyse): the hearing taps it from now on, and starts over. */
export function hearSource(ctx: AudioContext, node: AudioNode): void {
  tap = null
  queue = []
  queued = 0
  tapRate = ctx.sampleRate
  tapEpoch += 1
  bound?.analyzer.resetDrivers() // a real source change (never a stall): in worker mode this fences the old tap's blocks
  orb?.reset()
  void attachTap(ctx, node, tapEpoch)
}

async function attachTap(ctx: AudioContext, node: AudioNode, epoch: number): Promise<void> {
  try {
    const t = await createProcessorNode(ctx, { script: '/pcm-tap-worklet.js', name: 'pcm-tap', numberOfInputs: 1, outputChannels: 1 })
    if (epoch !== tapEpoch || ctx.state === 'closed') return // the source changed while loading
    // a node only runs while it reaches the destination — through a silent gain (no echo)
    const sink = ctx.createGain()
    sink.gain.value = 0
    node.connect(t)
    t.connect(sink)
    sink.connect(ctx.destination)
    t.port.onmessage = (e: MessageEvent<Float32Array | { orbL: Float32Array; orbR: Float32Array }>) => {
      if (epoch !== tapEpoch) return
      if (!(e.data instanceof Float32Array)) { if (orbPcm) orb?.push(e.data.orbL, e.data.orbR, tapRate); return }
      if (!gateOn || bound?.mode !== 'thread') return
      queue.push(e.data)
      queued += e.data.length
      while (queued > HEARING_MAX_QUEUED_SEC * tapRate && queue.length > 1) queued -= queue.shift()!.length
    }
    t.port.postMessage({ on: gateOn, orb: orbPcm })
    tap = t
    routeTap()
  } catch (err) {
    console.warn('[syqel] hearing tap unavailable — fx worlds run on idle drivers', err)
  }
}

/** Point the tap at where the student runs: a fresh port into the worker, or (null) back to the main thread. */
function routeTap(): void {
  if (!tap) return
  const client = bound?.mode === 'worker' ? bound.client : null
  if (!client) { tap.port.postMessage({ hearingPort: null }); return }
  const { port1, port2 } = new MessageChannel()
  client.attachAudioPort(port1)
  tap.port.postMessage({ hearingPort: port2 }, [port2])
}

/** Open/close the tap's gate (only a change does anything). */
function setGate(on: boolean): void {
  if (on === gateOn) return
  gateOn = on
  queue = []
  queued = 0
  tap?.port.postMessage({ on })
}

let weights: Promise<ArrayBuffer | null> | null = null
let reading: string | null = null
/** The attribution weights, fetched once on first use — never on the first-render path. */
function hearingWeights(): Promise<ArrayBuffer | null> {
  weights ??= fetch(HEARING_WEIGHTS_URL)
    .then((r) => (r.ok ? r.arrayBuffer() : null))
    .catch(() => null)
    .then((b) => {
      if (!b) console.warn('[syqel] hearing weights unavailable — fx worlds run on idle drivers')
      else reading = readingOf(b)
      return b
    })
  return weights
}

/** The live reading the engine picks for these weights (its head list decides) — null where the page's engine
 *  predates profiles or refuses the file. Parsed once, on the copy the hearing keeps (the worker gets a clone). */
function readingOf(w: ArrayBuffer): string | null {
  if (typeof Engine.parseAttributionWeights !== 'function' || typeof Engine.liveDriverProfile !== 'function') return null
  const model = Engine.parseAttributionWeights(w)
  return model ? Engine.liveDriverProfile(model.heads).id : null
}

/** The student in a worker, or null (with the reason logged) where that cannot run here. */
async function startWorkerSource(analyzer: Analyzer, w: ArrayBuffer, tag: string): Promise<DriverWorkerClient | null> {
  // the page's engine is whatever OTA activated — read the client off it at call time, and only if it has one
  if (typeof Worker !== 'function' || typeof Engine.DriverWorkerClient !== 'function' || typeof analyzer.setDriverSource !== 'function') {
    console.log(`${tag} hearing in-thread (no worker support here)`)
    return null
  }
  let worker: Worker
  try {
    worker = await startHearingWorker()
  } catch (err) {
    console.warn(`${tag} hearing worker failed to load — hearing in-thread`, err)
    return null
  }
  const client = new Engine.DriverWorkerClient(worker, w)
  const failed = new Promise<false>((res) => {
    worker.addEventListener('error', (e) => { e.preventDefault(); res(false) }, { once: true })
    setTimeout(() => res(false), WORKER_START_TIMEOUT_MS)
  })
  if (await Promise.race([client.ready, failed])) return client
  console.warn(`${tag} hearing worker refused (${client.error ?? 'failed to start'}) — hearing in-thread`)
  client.dispose()
  return null
}

/** Decide where this binding's student runs and install it. */
async function start(b: Binding, tag: string): Promise<void> {
  const w = await hearingWeights()
  if (bound !== b) return // disposed (or replaced) while the weights loaded
  if (!w) { b.mode = 'off'; return }
  const client = await startWorkerSource(b.analyzer, w, tag)
  if (bound !== b) { client?.dispose(); return }
  if (client) {
    b.analyzer.setDriverSource!(client)
    b.client = client
    b.busySeen = 0
    b.mode = 'worker'
    console.log(`${tag} hearing in a worker`)
  } else if (b.analyzer.setAttributionWeights(w)) {
    b.mode = 'thread'
  } else {
    console.warn(`${tag} hearing weights refused by the engine`)
    b.mode = 'off'
  }
  routeTap()
}

/**
 * Bind the hearing to a render loop's analyzer (one at a time — a new binding replaces the last). The student
 * starts the first time `listen(true)` is called, never on the first-render path. `tag` prefixes its logs.
 */
export function bindHearing(analyzer: Analyzer, tag: string): Hearing {
  if (bound) disposeBinding(bound)
  const b: Binding = { analyzer, mode: 'pending', client: null, busyMs: 0, busySeen: 0 }
  bound = b
  let started = false
  let statsAt = performance.now()
  return {
    feed() {
      if (bound !== b || !gateOn || b.mode !== 'thread') return
      const t0 = performance.now()
      for (const block of queue) analyzer.pushPcm(block, tapRate)
      b.busyMs += performance.now() - t0
      queue = []
      queued = 0
    },
    listen(on) {
      if (bound !== b) return
      setGate(on)
      if (on && !started) { started = true; void start(b, tag) }
    },
    stats() {
      const now = performance.now()
      const sec = Math.max(1e-3, (now - statsAt) / 1000)
      statsAt = now
      const total = b.client ? b.client.stats.busyMs : b.busyMs
      const busy = total - b.busySeen
      b.busySeen = total
      return { mode: b.mode, busyMsPerSec: Math.round((busy / sec) * 10) / 10, reading }
    },
    dispose() {
      if (bound === b) disposeBinding(b)
    }
  }
}

function disposeBinding(b: Binding): void {
  bound = null
  setGate(false)
  if (b.client) {
    b.analyzer.setDriverSource?.(null)
    b.client.dispose() // terminates the worker
    b.client = null
  }
  b.mode = 'off'
  routeTap() // the tap posts to the main thread again (and nowhere while the gate is shut)
}
